<script>
export default {
  name: 'RoundRadioButton',
  props: {
    item: String,
    name: String,
    checked: Boolean,
    subtext: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    subtextClass: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: true,
    },
    showBorderOnHover: {
      type: Boolean,
      default: true,
    },
    value: [String, Boolean, Number],
    handleClick: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    displayAsRequired: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value),
    },
    showBackgroundWhenSelected: {
      type: Boolean,
      default: true,
    },
  },
};
</script>

<template>
  <div
    class="flex flex-grow p-0 transition-colors duration-200"
    :class="[
      show && checked && showBackgroundWhenSelected
        ? 'activeRadioButton'
        : show && checked && !showBackgroundWhenSelected
          ? 'activeRadioButtonNoBackground'
          : displayAsRequired && show && !disabled
            ? 'border-red-100 bg-red-100 hover:bg-off-white'
            : displayAsRequired && show && disabled
              ? 'border-red-100 bg-red-100'
              : 'border-transparent',
      showBorderOnHover && !disabled
        ? 'border-l-4 border-transparent hover:border-light-gray'
        : showBorderOnHover && disabled
          ? 'border-l-4 border-transparent'
          : 'rounded-[5px]',
    ]"
  >
    <div
      class="container-radio group w-full"
      :class="{
        'text-dark-gray': show && checked,
        disabled: disabled,
        size,
      }"
    >
      <label
        class="circle-hit-area"
        :class="size"
      >
        <slot />
        <input
          type="radio"
          :value="value"
          :name="name"
          :checked="show && checked"
          :disabled="disabled"
          @click="() => (disabled ? null : handleClick(value))"
        />
        <span
          v-if="!showBorderOnHover"
          class="checkmark border-2 border-transparent"
          :class="[
            !(show && checked) && !disabled
              ? 'group-hover:bg-steel-300'
              : 'checked-no-hover-border',
          ]"
        />
      </label>
      <div class="container-radio-text flex min-w-0 flex-1 flex-col">
        <div class="flex items-center gap-0.5">
          <span class="flex shrink-0 items-center [&_img]:h-4 [&_img]:w-4">
            <slot name="item-icon" />
          </span>
          <p
            class="text-1md"
            :class="{ 'text-dark-gray': checked, [customClass]: customClass }"
          >
            {{ item }}
          </p>
        </div>
        <p
          v-if="!!subtext.length"
          class="text-xxs leading-3"
          :class="
            checked
              ? `text-dark-gray ${subtextClass}`
              : `text-body-color ${subtextClass}`
          "
          style="margin-top: 3px; margin-bottom: 3px"
          v-html="subtext"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom CSS for Checkboxes */
/* Template used from W3 schools https://www.w3schools.com/howto/tryit.asp?filename=tryhow_css_custom_checkbox */
/* The container – flex row: [circle] [text] */
.container-radio {
  position: relative;
  display: flex;
  height: 100%;
  align-items: center;
  text-align: left;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #6d7181;
  min-height: 80px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Clickable area: only the circle (label wraps input + checkmark) */
.circle-hit-area {
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
}
.circle-hit-area.small {
  width: 2rem;
  min-height: 40px;
}
.circle-hit-area.medium {
  width: 3rem;
  min-height: 60px;
}
.circle-hit-area.large {
  width: 4rem;
  min-height: 80px;
}
@media (max-width: 768px) {
  .circle-hit-area.small {
    width: 3rem;
    min-height: 60px;
  }
  .circle-hit-area.medium {
    width: 4rem;
    min-height: 80px;
  }
  .circle-hit-area.large {
    width: 5rem;
    min-height: 100px;
  }
}

.circle-hit-area input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  margin: 0;
}

/* Custom checkmark (circle) – positioned inside .circle-hit-area */
.circle-hit-area .checkmark {
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translate(0, -50%);
  height: 2rem;
  width: 2rem;
  border-radius: 9999px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  background-color: #ededf0;
}
.circle-hit-area.small .checkmark {
  height: 1rem;
  width: 1rem;
}
.circle-hit-area.medium .checkmark {
  height: 1.5rem;
  width: 1.5rem;
}
.circle-hit-area.large .checkmark {
  height: 2rem;
  width: 2rem;
}
@media (max-width: 768px) {
  .circle-hit-area.small .checkmark {
    height: 1.5rem;
    width: 1.5rem;
  }
  .circle-hit-area.medium .checkmark {
    height: 2rem;
    width: 2rem;
  }
  .circle-hit-area.large .checkmark {
    height: 2.5rem;
    width: 2.5rem;
  }
}

/* When the checkbox is checked, add a yellow background */
.container-radio input:checked ~ .checkmark {
  border-color: transparent;
  background-color: #f8c617;
}

/* When the checkbox is not checked, show border */
.container-radio input:not(:checked) ~ .checkmark {
  border-color: var(--color-steel-300);
}

/* Prevent hover border on checked radio buttons */
.container-radio input:checked ~ .checkmark:hover {
  border-color: transparent !important;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container-radio input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container-radio .checkmark:after {
  content: '';
  position: absolute;
  display: none;
  width: 0.75rem;
  height: 0.75rem;
  background-color: #000;
  border: solid #000;
  border-radius: 9999px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Mobile responsive checkmark indicator sizing */
@media (max-width: 768px) {
  .small .checkmark:after {
    width: 0.5rem;
    height: 0.5rem;
  }

  .medium .checkmark:after {
    width: 0.75rem;
    height: 0.75rem;
  }

  .large .checkmark:after {
    width: 1rem;
    height: 1rem;
  }
}

.activeRadioButton {
  border-color: #f8c617;
  background-color: #ededf0;
}

.activeRadioButtonNoBackground {
  border-color: #f8c617;
}

/* Custom hover effect for radio button container */
.container-radio:hover {
  transition: background-color 0.2s ease;
}

/* Disable hover effects when radio button is disabled */
.container-radio.disabled:hover {
  background-color: transparent !important;
  cursor: not-allowed !important;
}

.container-radio input:disabled ~ .checkmark {
  opacity: 0.5;
  cursor: not-allowed;
}

.container-radio input:disabled {
  cursor: not-allowed;
}

.container-radio.disabled {
  cursor: not-allowed !important;
  opacity: 0.6;
}

/* Prevent any hover color changes on disabled radio buttons */
.container-radio.disabled:hover * {
  color: inherit !important;
}

/* Override Tailwind group-hover classes when disabled - maintain original checkmark color */
.container-radio.disabled .checkmark {
  background-color: #ededf0 !important;
}

.container-radio.disabled:hover .checkmark {
  background-color: #ededf0 !important;
}

.container-radio.disabled .checkmark:hover {
  background-color: #ededf0 !important;
}

/* Prevent group-hover effects when disabled */
.container-radio.disabled.group:hover .checkmark {
  background-color: #ededf0 !important;
}

.container-radio.disabled input:disabled ~ .checkmark:hover {
  background-color: #ededf0 !important;
  border-color: inherit !important;
}

/* If checked when disabled, maintain yellow background */
.container-radio.disabled input:checked ~ .checkmark {
  background-color: #f8c617 !important;
}

.container-radio.disabled:hover input:checked ~ .checkmark {
  background-color: #f8c617 !important;
}

.container-radio.disabled input:checked ~ .checkmark:hover {
  background-color: #f8c617 !important;
}
</style>
